import { t } from "i18next";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useFetch } from "../../../../../hooks/useFetch";
import { DeviceModel, SelectOption } from "../../../../../types/types";
import { ScrollArea } from "../../../../../ui/ScrollArea/ScrollArea";
import { Separator } from "../../../../../ui/Separator";
import { Button } from "../../../../../ui/Button";
import { Input } from "../../../../../ui/Input/Input";
import { useAppStore } from "../../../../../stores/RootStore";
import { useOutletContext } from "react-router-dom";
import DeleteModel from "../../DeleteModel";
import MultipleSelector from "../../../../../ui/MultipleSelector/MultipleSelector";
import { Label } from "../../../../../ui/Label/Label";
import Spinner from "../../../../../ui/Spinner/Spinner";

type Props = {
  id: string | undefined;
  currentModel: DeviceModel | undefined;
  sensors: SelectOption[];
};

const ModelSettingsTab = ({ id, currentModel, sensors }: Props) => {
  //const [errors, setErrors] = useState([]);

  const [models, setModels] = useOutletContext<[DeviceModel[], Function]>();

  let [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { handleSubmit, reset, control, getValues } = useForm({
    defaultValues: {
      name: currentModel?.attributes.name,
      slot_1: currentModel?.attributes.structure.slots.find((slot) => slot.number == 1)
        ? currentModel?.attributes.structure.slots
            .find((slot) => slot.number == 1)
            ?.sensors.map((sensor) => {
              return { value: sensor, label: sensors?.find((fsensor) => fsensor.value == sensor)?.label! };
            })
        : [],
      slot_2: currentModel?.attributes.structure.slots.find((slot) => slot.number == 2)
        ? currentModel?.attributes.structure.slots
            .find((slot) => slot.number == 2)
            ?.sensors.map((sensor) => {
              return { value: sensor, label: sensors?.find((fsensor) => fsensor.value == sensor)?.label! };
            })
        : [],
      slot_3: currentModel?.attributes.structure.slots.find((slot) => slot.number == 3)
        ? currentModel?.attributes.structure.slots
            .find((slot) => slot.number == 3)
            ?.sensors.map((sensor) => {
              return { value: sensor, label: sensors?.find((fsensor) => fsensor.value == sensor)?.label! };
            })
        : [],
      slot_4: currentModel?.attributes.structure.slots.find((slot) => slot.number == 4)
        ? currentModel?.attributes.structure.slots
            .find((slot) => slot.number == 4)
            ?.sensors.map((sensor) => {
              return { value: sensor, label: sensors?.find((fsensor) => fsensor.value == sensor)?.label! };
            })
        : [],
    },
  });

  function openDeleteModal() {
    setIsDeleteOpen(true);
  }

  function closeDeleteModal() {
    setIsDeleteOpen(false);
  }

  const [{ response: saveResponse, isLoading: saveIsLoading, error: saveError }, doFetchItemSave] = useFetch(
    `/administration/directories/device_models/${id}`,
    true
  );

  const onSubmit = (formData: any) => {
    const slots = [];
    if (formData.slot_1.length > 0) slots.push({ number: 1, sensors: formData.slot_1.map((item: SelectOption) => item.value) });
    if (formData.slot_2.length > 0) slots.push({ number: 2, sensors: formData.slot_2.map((item: SelectOption) => item.value) });
    if (formData.slot_3.length > 0) slots.push({ number: 3, sensors: formData.slot_3.map((item: SelectOption) => item.value) });
    if (formData.slot_4.length > 0) slots.push({ number: 4, sensors: formData.slot_4.map((item: SelectOption) => item.value) });

    doFetchItemSave({
      method: "PUT",
      data: {
        data: {
          name: formData.name,
          structure: {
            slots: slots,
          },
        },
      },
    });
  };

  const editModel = (itemSaveResponse: { data: unknown }) => {
    const newUserData = itemSaveResponse.data as unknown as DeviceModel;
    const withoutCurrentItem = models?.filter((model) => model.id != currentModel?.id)!;
    setModels([...withoutCurrentItem, newUserData].sort((a, b) => Number(a.id) - Number(b.id)));
  };

  useEffect(() => {
    if (!saveResponse) return;
    editModel(saveResponse);
  }, [saveResponse]);

  return (
    <>
      <div className="pt-8">
        <form onSubmit={handleSubmit(onSubmit)}>
          <ScrollArea>
            <div className="flex flex-col gap-3">
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Input {...field} label={`${t("title_name")}*`} />
                  </div>
                )}
              />
              <Controller
                name="slot_1"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Label>{t("sensors_for_slot 1")}*</Label>
                    <MultipleSelector value={field.value} onChange={field.onChange} defaultOptions={sensors} />
                  </div>
                )}
              />
              <Controller
                name="slot_2"
                control={control}
                //rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Label>{t("sensors_for_slot 2")}</Label>
                    <MultipleSelector value={field.value} onChange={field.onChange} defaultOptions={sensors} />
                  </div>
                )}
              />
              <Controller
                name="slot_3"
                control={control}
                //rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Label>{t("sensors_for_slot 3")}</Label>
                    <MultipleSelector value={field.value} onChange={field.onChange} defaultOptions={sensors} />
                  </div>
                )}
              />
              <Controller
                name="slot_4"
                control={control}
                //rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Label>{t("sensors_for_slot 4")}</Label>
                    <MultipleSelector value={field.value} onChange={field.onChange} defaultOptions={sensors} />
                  </div>
                )}
              />

              <Separator />
              <div className="mt-8 flex w-full justify-between">
                <Button variant={"destructive"} size={"sm"} type="button" onClick={() => openDeleteModal()}>
                  {t("delete")}
                </Button>
                <Button variant={"default"} size={"sm"} type="submit">
                  {t("save")}
                </Button>
              </div>
            </div>
          </ScrollArea>
        </form>
      </div>
      <DeleteModel id={id!} isDeleteOpen={isDeleteOpen} closeModal={closeDeleteModal} />
    </>
  );
};

export default ModelSettingsTab;
